import React, { useEffect, useRef, useCallback, useState } from 'react';
import htmlLogo from '../images/html-logo.png';
import cssLogo from '../images/css-logo.png';
import reactLogo from '../images/react-logo.png';
import nodejsLogo from '../images/nodejs-logo.png';
import mongodbLogo from '../images/mongodb-logo.png';
import mysqlLogo from '../images/mysql-logo.png';
import bootstrapLogo from '../images/bootstrap-logo.png';
import materialUILogo from '../images/materialUI-logo.png';
import tailwindLogo from '../images/tailwind-css-logo.png';
import javaLogo from '../images/java-logo.png';

const skills = [
  { name: 'HTML5', img: htmlLogo },
  { name: 'CSS3', img: cssLogo },
  { name: 'React', img: reactLogo },
  { name: 'Node.js', img: nodejsLogo },
  { name: 'MongoDB', img: mongodbLogo },
  { name: 'MySQL', img: mysqlLogo },
  { name: 'Bootstrap', img: bootstrapLogo },
  { name: 'Material UI', img: materialUILogo },
  { name: 'Tailwind CSS', img: tailwindLogo },
  { name: 'Java', img: javaLogo },
];

function getItemSize() {
  if (window.innerWidth <= 480) return 55;
  if (window.innerWidth <= 768) return 65;
  return 85;
}

function Skills() {
  const containerRef = useRef(null);
  const itemsRef = useRef([]);
  const positionsRef = useRef([]);
  const velocitiesRef = useRef([]);
  const animFrameRef = useRef(null);
  const [itemSize, setItemSize] = useState(getItemSize());

  const initPositions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const size = getItemSize();
    const positions = [];
    const velocities = [];

    skills.forEach(() => {
      positions.push({
        x: Math.random() * (rect.width - size - 20) + 10,
        y: Math.random() * (rect.height - size - 20) + 10,
      });
      velocities.push({
        vx: (Math.random() - 0.5) * 1.2,
        vy: (Math.random() - 0.5) * 1.2,
      });
    });

    positionsRef.current = positions;
    velocitiesRef.current = velocities;
  }, []);

  const animate = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const size = getItemSize();

    positionsRef.current.forEach((pos, i) => {
      const vel = velocitiesRef.current[i];
      pos.x += vel.vx;
      pos.y += vel.vy;

      // Bounce off walls
      if (pos.x <= 0 || pos.x >= rect.width - size) {
        vel.vx *= -1;
        pos.x = Math.max(0, Math.min(pos.x, rect.width - size));
      }
      if (pos.y <= 0 || pos.y >= rect.height - size) {
        vel.vy *= -1;
        pos.y = Math.max(0, Math.min(pos.y, rect.height - size));
      }

      // Slight floating drift
      vel.vx += (Math.random() - 0.5) * 0.015;
      vel.vy += (Math.random() - 0.5) * 0.015;

      // Clamp velocity
      const maxSpeed = 1.2;
      vel.vx = Math.max(-maxSpeed, Math.min(maxSpeed, vel.vx));
      vel.vy = Math.max(-maxSpeed, Math.min(maxSpeed, vel.vy));
    });

    // Update DOM directly (no React re-render)
    itemsRef.current.forEach((el, i) => {
      if (el && positionsRef.current[i]) {
        el.style.transform = `translate3d(${positionsRef.current[i].x}px, ${positionsRef.current[i].y}px, 0)`;
      }
    });

    animFrameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    initPositions();
    animFrameRef.current = requestAnimationFrame(animate);

    const handleResize = () => {
      setItemSize(getItemSize());
      initPositions();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [initPositions, animate]);

  return (
    <section className='skills-section' id='skills'>
      <div className='skills-heading'>
        <h1 className='skills-title'>My Arsenal</h1>
        <p className='skills-subtitle'>Explore the tools I wield</p>
      </div>

      <div className='skills-canvas' ref={containerRef}>
        {skills.map((skill, i) => (
          <div
            key={skill.name}
            className='skill-bubble'
            ref={(el) => (itemsRef.current[i] = el)}
            style={{ width: itemSize, height: itemSize }}
          >
            <img src={skill.img} alt={skill.name} draggable={false} />
            <span className='skill-name'>{skill.name}</span>
          </div>
        ))}
      </div>

      <style>{`
        .skills-section {
          width: 100%;
        //   min-height: 100vh;
        //   padding: 4rem 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .skills-heading {
          text-align: center;
          margin-bottom: 2rem;
          z-index: 2;
          padding: 0 1rem;
        }

        .skills-title {
          font-size: clamp(2rem, 6vw, 3.5rem);
          font-weight: 800;
          background: linear-gradient(135deg, #fff 0%, #1ecd97 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
        }

        .skills-subtitle {
          font-size: clamp(0.85rem, 2.5vw, 1.1rem);
          color: rgba(255, 255, 255, 0.5);
          margin-top: 0.5rem;
          letter-spacing: 1px;
        }

        .skills-canvas {
          position: relative;
          width: 100%;
          height: clamp(350px, 60vh, 600px);
          border-radius: 0;
          background: radial-gradient(ellipse at center, rgba(30, 205, 151, 0.04) 0%, transparent 70%);
          // border-top: 1px solid rgba(255, 255, 255, 0.05);
          // border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          overflow: hidden;
          user-select: none;
        }

        .skill-bubble {
          position: absolute;
          top: 0;
          left: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 1;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          will-change: transform;
          transition: box-shadow 0.3s, border-color 0.3s;
        }

        .skill-bubble:hover {
          box-shadow: 0 0 25px rgba(30, 205, 151, 0.3), 0 0 60px rgba(30, 205, 151, 0.1);
          border-color: rgba(30, 205, 151, 0.4);
        }

        .skill-bubble img {
          width: 50%;
          height: 50%;
          object-fit: contain;
          pointer-events: none;
          -webkit-user-drag: none;
        }

        .skill-name {
          position: absolute;
          bottom: -20px;
          font-size: clamp(0.55rem, 1.5vw, 0.7rem);
          color: rgba(255, 255, 255, 0.8);
          white-space: nowrap;
          letter-spacing: 0.5px;
          opacity: 0;
          transition: opacity 0.3s;
          pointer-events: none;
        }

        .skill-bubble:hover .skill-name {
          opacity: 1;
        }

        @media (max-width: 480px) {
          .skills-section {
            padding: 2rem 0;
          }
          .skills-canvas {
            height: 300px;
          }
        }
      `}</style>
    </section>
  );
}

export default Skills;
